// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { LicenseValuesForm } from './LicenseForm';
import { LicenseForm } from './LicenseForm';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';

interface LicenseManageProps {
    onFinish: (license: string) => void;
    triggerElem?: ReactElement;
}

const LicenseManage = ({ onFinish, triggerElem }: LicenseManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const { data: challenge, refetch: refetchChallenge } = useGenericQuery<string>({
        fetchCallback: () => client.LicenseController.getChallenge(),
        key: ['get-license-challenge'],
        manual: true,
        fetchOnStart: false,
    });

    const onFinishHandler = ({ license = '' }: LicenseValuesForm): void => {
        const licenseProcessed = license.trim();

        if (licenseProcessed) {
            onFinish(licenseProcessed);
        }
    };

    useEffect(() => {
        form.setFieldsValue({ challenge });
    }, [form, challenge]);

    return (
        <THDrawer
            title={i(`license.set.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                visible && refetchChallenge();
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <LicenseForm
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    challenge={challenge}
                />
            )}
        </THDrawer>
    );
};

export default LicenseManage;
